import {
    Component,
    ContentChild,
    Input,
    OnInit,
    TemplateRef,
} from "@angular/core";
import { AsyncPipe, NgTemplateOutlet } from "@angular/common";
import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { MatSidenavModule } from "@angular/material/sidenav";
import { debounceTime, Observable } from "rxjs";
import { map, shareReplay, startWith, tap } from "rxjs/operators";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { FilterService } from "@/_services/filter.service";
import { GroupsService } from "@/_services/groups.service";
import { UserService } from "@/_services/user.service";
import {
    buildPastDaysDateRange,
    reapplyTimeFrameRange,
} from "@/_utility/date.utils";
import {
    GroupFilterOptions,
    toStatsGroupParams,
    withoutGroupFilters,
} from "@/_models/filters/group-filter-options";
import { RadioOption } from "@/components/shared/radio-list/radio-list.component";
import { timeFrameListValues } from "@/components/safety-assurance/issue-list/issue-list.component";
import { SharedComponentsModule } from "@/components/shared/shared-components.module";
import { PerformanceFilterSidebarComponent } from "@/components/statistics/performance-filter-sidebar/performance-filter-sidebar.component";
import { PerformanceFilterTopbarComponent } from "@/components/statistics/performance-filter-topbar/performance-filter-topbar.component";
import { isEnabled } from "@features/functions";

export type PerformanceStatsLoader<T> = (
    filters: Record<string, any>,
) => Observable<T>;

const ALL_TIME = "";

@UntilDestroy()
@Component({
    selector: "app-performance-tab",
    standalone: true,
    imports: [
        AsyncPipe,
        NgTemplateOutlet,
        MatSidenavModule,
        SharedComponentsModule,
        PerformanceFilterSidebarComponent,
        PerformanceFilterTopbarComponent,
    ],
    templateUrl: "./performance-tab.component.html",
    styleUrls: ["./performance-tab.component.scss"],
})
export class PerformanceTabComponent<T> implements OnInit {
    @Input({ required: true }) storageKey: string;
    @Input({ required: true }) loadStats: PerformanceStatsLoader<T>;
    @Input() defaultPastDays: number | null = -30;
    @ContentChild(TemplateRef) statsTemplate: TemplateRef<unknown>;

    stats$: Observable<T>;
    filterForm: UntypedFormGroup;
    timeFrameList: RadioOption[] = timeFrameListValues;
    groupsEnabled = isEnabled("groupsLocations");
    groupFilterOptions$: Observable<GroupFilterOptions>;
    userList$: Observable<RadioOption[]>;
    currentFilters;
    statsFilters: Record<string, any>;

    constructor(
        private filterService: FilterService,
        private formBuilder: UntypedFormBuilder,
        private groupsService: GroupsService,
        private userService: UserService,
    ) {}

    ngOnInit(): void {
        this.buildFilterForm();

        this.userList$ = this.userService.getUsers().pipe(
            map((users) =>
                users.map((user) => ({
                    label: user.fullName,
                    value: user._id,
                })),
            ),
            shareReplay(),
        );

        if (this.groupsEnabled) {
            this.groupFilterOptions$ = this.groupsService
                .getGroupFilterOptions()
                .pipe(shareReplay());
        }
    }

    private initialFilters() {
        if (this.defaultPastDays == null) {
            return { timeFrame: ALL_TIME, createdAt: ALL_TIME, individual: "" };
        }
        return {
            timeFrame: this.defaultPastDays,
            createdAt: buildPastDaysDateRange(this.defaultPastDays),
            individual: "",
        };
    }

    private buildFilterForm() {
        const savedFilters = this.filterService.loadSimpleFilters(
            this.storageKey,
        );
        const filters = { ...this.initialFilters(), ...(savedFilters ?? {}) };

        this.filterForm = this.formBuilder.group({
            timeFrame: [filters.timeFrame],
            createdAt: [filters.createdAt],
            individual: [filters.individual],
        });

        if (this.groupsEnabled) {
            this.filterForm.addControl("groups", this.formBuilder.control([]));
        }

        reapplyTimeFrameRange(this.filterForm, "createdAt");

        this.filterForm.valueChanges
            .pipe(
                startWith(this.filterForm.value),
                debounceTime(500),
                untilDestroyed(this),
                tap((changes) => {
                    this.currentFilters = { ...changes };
                    this.filterService.saveSimpleFilters(
                        this.storageKey,
                        withoutGroupFilters({ ...changes }),
                    );
                    this.statsFilters = toStatsGroupParams(
                        this.filterForm.value,
                    );
                    this.stats$ = this.loadStats(this.statsFilters).pipe(
                        shareReplay(),
                    );
                }),
            )
            .subscribe();
    }
}
